import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_TableInstance } from 'material-react-table'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { MrtMobileCardConfig, MrtMobileFilter } from './mrtTypes.ts'

interface Props<T extends Record<string, any>> {
  open: boolean
  onClose: () => void
  config: {
    columns: MRT_ColumnDef<T, any>[]
    mobileConfig?: MrtMobileCardConfig<T>
  }
  table: MRT_TableInstance<T>
  filters: MRT_ColumnFiltersState
}

interface ResolvedMobileFilter<T extends Record<string, any>> {
  id: string
  label: string
  variant?: MRT_ColumnDef<T, any>['filterVariant']
  options?: Array<
    | string
    | {
        label: string
        value: string | number
      }
  >
}

/** Obtiene el id de la columna */
const getColumnId = <T extends Record<string, any>>(column: MRT_ColumnDef<T, any>): string | undefined => {
  if (column.id) {
    return column.id
  }
  if (typeof column.accessorKey === 'string') {
    return column.accessorKey
  }
  return undefined
}

/** Texto humanizado */
const humanizeLabel = (value: string): string => {
  const lastPart = value.split('.').pop() ?? value

  return lastPart.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())
}

const normalizeMrtOptions = <T extends Record<string, any>>(
  raw: MRT_ColumnDef<T, any>['filterSelectOptions'],
): ResolvedMobileFilter<T>['options'] => {
  if (!Array.isArray(raw)) {
    return undefined
  }

  return raw.map(
    (
      option,
    ):
      | string
      | {
          label: string
          value: string | number
        } => {
      if (typeof option === 'string' || typeof option === 'number') {
        return String(option)
      }

      const record = option as Record<string, unknown>
      const value = (record.value ?? record.text ?? record.label) as string | number
      const label = (record.label ?? record.text ?? String(value)) as string
      return {
        label,
        value,
      }
    },
  )
}

/** Resolver de los filtros móbiles */
const resolveMobileFilters = <T extends Record<string, any>>(
  filters: MrtMobileFilter<T>[] | undefined,
  columns: MRT_ColumnDef<T, any>[],
): ResolvedMobileFilter<T>[] => {
  if (!filters?.length) {
    return []
  }

  const columnMap = new Map<string, MRT_ColumnDef<T, any>>()

  for (const column of columns) {
    const id = getColumnId(column)

    if (id) {
      columnMap.set(id, column)
    }
  }

  return filters.flatMap((definition) => {
    const field = typeof definition === 'string' ? definition : definition.field
    const customConfig = typeof definition === 'string' ? undefined : definition
    const column = columnMap.get(field)
    /**
     * Si la columna MRT explícitamente
     * deshabilita el filtro, no la mostramos.
     */
    if (column?.enableColumnFilter === false) {
      return []
    }
    return [
      {
        id: field,
        label:
          customConfig?.label ?? (typeof column?.header === 'string' ? column.header : humanizeLabel(field)),
        variant: customConfig?.variant ?? column?.filterVariant,
        options: customConfig?.options ?? normalizeMrtOptions(column?.filterSelectOptions),
      },
    ]
  })
}

/** Funcion principal del cajon de filtros */
function MrtMobileFilterDrawerComponent<T extends Record<string, any>>({
  open,
  onClose,
  config,
  table,
  filters,
}: Props<T>) {
  const mobileConfig = config.mobileConfig

  const mobileFilters = useMemo(
    () => resolveMobileFilters(mobileConfig?.filters, config.columns),
    [mobileConfig?.filters, config.columns],
  )

  /**
   * Filtros temporales.
   *
   * IMPORTANTE:
   *
   * Estos filtros NO modifican MRT mientras
   * el usuario está editando.
   */
  const [draftFilters, setDraftFilters] = useState<MRT_ColumnFiltersState>([])

  /* ------------------------------------------------------------------------ */
  /* SYNC WHEN OPEN                                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!open) {
      return
    }

    /**
     * Copiamos los filtros actuales de MRT.
     *
     * De esta manera el Drawer siempre comienza
     * mostrando el estado real aplicado.
     */
    setDraftFilters(filters)
  }, [open, filters])

  const getDraftValue = useCallback(
    (columnId: string): unknown => {
      return draftFilters.find((filter) => filter.id === columnId)?.value ?? ''
    },
    [draftFilters],
  )

  const updateDraftFilter = useCallback((columnId: string, value: unknown) => {
    setDraftFilters((previous) => {
      const current = previous.filter((filter) => filter.id !== columnId)

      /**
       * Valor vacío significa eliminar
       * ese filtro del draft.
       */
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return current
      }

      return [
        ...current,
        {
          id: columnId,
          value,
        },
      ]
    })
  }, [])

  const clearDraftFilters = useCallback(() => {
    setDraftFilters([])
  }, [])

  /* ------------------------------------------------------------------------ */
  /* CANCEL                                                                    */
  /* ------------------------------------------------------------------------ */

  const handleClose = useCallback(() => {
    /**
     * No modificamos MRT.
     *
     * Simplemente cerramos el Drawer.
     * Al volver a abrir se sincronizará nuevamente
     * con los filtros realmente aplicados.
     */
    onClose()
  }, [onClose])

  /* ------------------------------------------------------------------------ */
  /* APPLY                                                                     */
  /* ------------------------------------------------------------------------ */

  const handleApply = useCallback(() => {
    /**
     * AQUÍ es donde finalmente modificamos MRT.
     *
     * Esto provoca la actualización de la tabla
     * y, si corresponde, la nueva consulta al servidor.
     */
    table.setColumnFilters(draftFilters)

    onClose()
  }, [draftFilters, onClose, table])

  const draftFilterCount = draftFilters.length

  const appliedFilterCount = filters.length

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            maxHeight: '85vh',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: 'hidden',
            backgroundImage: 'none',
            boxShadow: theme.shadows[12],
          }),
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 1.25,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            mx: 'auto',
            mb: 1.5,
            borderRadius: 2,
            bgcolor: 'action.disabledBackground',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Filtros
            </Typography>

            {draftFilterCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {draftFilterCount} {draftFilterCount === 1 ? 'filtro seleccionado' : 'filtros seleccionados'}
              </Typography>
            )}
          </Box>

          {draftFilterCount > 0 && (
            <Button size="small" color="inherit" onClick={clearDraftFilters}>
              Limpiar
            </Button>
          )}
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          overflowY: 'auto',
          /**
           * Dejamos espacio para que el último
           * campo no quede pegado al footer.
           */
          pb: 3,
        }}
      >
        {!mobileFilters.length ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No hay filtros disponibles.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.75}>
            {mobileFilters.map((filter) => {
              const value = getDraftValue(filter.id)

              if (filter.variant === 'select' || filter.variant === 'multi-select') {
                const isMultiple = filter.variant === 'multi-select'

                return (
                  <FormControl key={filter.id} fullWidth size="small">
                    <InputLabel>{filter.label}</InputLabel>
                    <Select
                      multiple={isMultiple}
                      label={filter.label}
                      value={isMultiple ? (Array.isArray(value) ? value : []) : (value ?? '')}
                      onChange={(event) => {
                        updateDraftFilter(filter.id, event.target.value)
                      }}
                    >
                      {!isMultiple && <MenuItem value="">Todos</MenuItem>}

                      {filter.options?.map((option) => {
                        const optionValue = typeof option === 'string' ? option : option.value

                        const optionLabel = typeof option === 'string' ? option : option.label

                        return (
                          <MenuItem key={String(optionValue)} value={optionValue}>
                            {optionLabel}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )
              }

              return (
                <TextField
                  key={filter.id}
                  fullWidth
                  size="small"
                  label={filter.label}
                  value={value ?? ''}
                  onChange={(event) => {
                    updateDraftFilter(filter.id, event.target.value)
                  }}
                />
              )
            })}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Button fullWidth variant="outlined" color="inherit" onClick={handleClose}>
          Cancelar
        </Button>
        <Button fullWidth variant="contained" onClick={handleApply}>
          Aplicar filtros
          {draftFilterCount > 0 && ` (${draftFilterCount})`}
        </Button>
      </Box>
    </Drawer>
  )
}

/**
 * Drawer de filtros para dispositivos móviles.
 * @author isi-template
 */
export const MrtMobileFilterDrawer = memo(
  MrtMobileFilterDrawerComponent,
) as typeof MrtMobileFilterDrawerComponent
